import { useState, useEffect, useMemo } from 'react'
import { Table, Button, Input, Space, Tag, Popconfirm, List, Grid, Tooltip, Modal, Alert } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined, ToolOutlined, WarningOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import VehicleFormModal from '@/components/VehicleFormModal'
import { useVehicles, useDeleteVehicle } from '@/hooks/useVehicles'
import { useCustomers } from '@/hooks/useCustomers'
import { usePageTitle } from '@/contexts/PageTitleContext'
import type { Vehicle } from '@/types'
import VehicleMaintenanceDrawer from '@/components/VehicleMaintenanceDrawer'
import { usePermission } from '@/hooks/usePermission'

const { useBreakpoint } = Grid

const VehiclesPage = () => {
  const { setTitle } = usePageTitle()
  const canCreate = usePermission('vehicles.create')
  const canEdit   = usePermission('vehicles.edit')
  const canDelete = usePermission('vehicles.delete')
  const [searchText, setSearchText] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null)
  const [maintenanceVehicle, setMaintenanceVehicle] = useState<Vehicle | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortField, setSortField] = useState<string | undefined>('targa')
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>('ascend')
  const screens = useBreakpoint()
  const isMobile = !screens.md

  useEffect(() => {
    setTitle('Gestione Veicoli')
  }, [setTitle])

  // Recupera TUTTI i veicoli per il sorting
  const { data: allVehiclesData, isLoading } = useVehicles(1, 10000, searchText)
  const { data: customersData } = useCustomers(1, 1000)
  const deleteMutation = useDeleteVehicle()

  const sortedItems = useMemo(() => {
    if (!allVehiclesData?.items) return []

    let sortedData = [...allVehiclesData.items]

    if (sortField && sortOrder) {
      sortedData.sort((a, b) => {
        let aValue: any = a[sortField as keyof Vehicle]
        let bValue: any = b[sortField as keyof Vehicle]

        if (aValue == null && bValue == null) return 0
        if (aValue == null) return sortOrder === 'ascend' ? 1 : -1
        if (bValue == null) return sortOrder === 'ascend' ? -1 : 1

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = (bValue as string).toLowerCase()
          return sortOrder === 'ascend'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        }

        if (typeof aValue === 'number' || typeof aValue === 'boolean') {
          return sortOrder === 'ascend' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1)
        }

        return 0
      })
    }

    return sortedData
  }, [allVehiclesData?.items, sortField, sortOrder])

  const handleCreate = () => {
    setEditingVehicle(null)
    setIsModalOpen(true)
  }

  const handleEdit = (record: Vehicle) => {
    setEditingVehicle(record)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id)
  }

  const handleDeleteWithCourtesyWarning = (record: Vehicle) => {
    Modal.confirm({
      title: (
        <span>
          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
          Elimina veicolo auto di cortesia
        </span>
      ),
      content: (
        <div>
          <p>Stai per eliminare <strong>{record.targa} — {record.marca} {record.modello}</strong>.</p>
          <Alert
            type="warning"
            showIcon
            message="Questo veicolo è registrato come auto di cortesia. Eliminandolo verranno cancellati anche tutti i dati del contratto associato."
            style={{ marginTop: 8 }}
          />
        </div>
      ),
      okText: 'Elimina definitivamente',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: () => handleDelete(record.id),
    })
  }

  // Helper function per trovare il cliente per ID
  const getCustomerName = (customerId: number | undefined) => {
    if (!customersData?.items) return '-'
    const customer = customersData.items.find(c => c.id === customerId)
    if (!customer) return '-'
    if (customer.tipo?.toLowerCase() === 'azienda') {
      return customer.ragione_sociale || '-'
    }
    return `${customer.nome || ''} ${customer.cognome || ''}`.trim() || '-'
  }

  const columns: ColumnsType<Vehicle> = [
    {
      title: 'Targa',
      dataIndex: 'targa',
      key: 'targa',
      sorter: true,
      render: (text) => (
        <Space>
          <CarOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Veicolo',
      key: 'vehicle',
      sorter: true,
      render: (_, record) => `${record.marca} ${record.modello}`,
    },
    {
      title: 'Anno',
      dataIndex: 'anno',
      key: 'anno',
      sorter: true,
      render: (year) => year || '-',
    },
    {
      title: 'Colore',
      dataIndex: 'colore',
      key: 'colore',
      sorter: true,
      render: (colore) => colore || '-',
    },
    {
      title: 'Cliente',
      key: 'customer',
      sorter: true,
      render: (_, record) => getCustomerName(record.current_customer_id),
    },
    {
      title: 'KM Attuali',
      dataIndex: 'km_attuali',
      key: 'km_attuali',
      sorter: true,
      render: (km) => km ? `${km.toLocaleString()} km` : '-',
    },
    {
      title: 'Auto di Cortesia',
      dataIndex: 'courtesy_car',
      key: 'courtesy_car',
      sorter: true,
      render: (isCourtesyCar) => (
        <Tag color={isCourtesyCar ? 'green' : 'default'}>
          {isCourtesyCar ? '✅ Sì' : '❌ No'}
        </Tag>
      ),
      width: 140,
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Manutenzioni programmate">
            <Button
              type="link"
              icon={<ToolOutlined />}
              onClick={() => setMaintenanceVehicle(record)}
            >
              Manutenzioni
            </Button>
          </Tooltip>
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Modifica
            </Button>
          )}
          {canDelete && (
            record.courtesy_car ? (
              <Button
                type="link" danger icon={<DeleteOutlined />}
                onClick={() => handleDeleteWithCourtesyWarning(record)}
              >
                Elimina
              </Button>
            ) : (
              <Popconfirm
                title="Sei sicuro di voler eliminare questo veicolo?"
                onConfirm={() => handleDelete(record.id)}
                okText="Sì"
                cancelText="No"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  Elimina
                </Button>
              </Popconfirm>
            )
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="Cerca veicolo..."
          onSearch={setSearchText}
          style={{ flex: '1 1 200px', maxWidth: 300 }}
          allowClear
        />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Nuovo Veicolo
          </Button>
        )}
      </div>

      {isMobile ? (
        <List
          loading={isLoading}
          dataSource={allVehiclesData?.items}
          rowKey="id"
          renderItem={(record) => (
            <List.Item
              style={{ padding: '10px 0' }}
              actions={[
                ...(canEdit ? [<Button
                  key="edit"
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />] : []),
                ...(canDelete ? [
                  record.courtesy_car ? (
                    <Button
                      key="delete"
                      type="link" danger size="small" icon={<DeleteOutlined />}
                      onClick={() => handleDeleteWithCourtesyWarning(record)}
                    />
                  ) : (
                    <Popconfirm
                      key="delete"
                      title="Eliminare questo veicolo?"
                      onConfirm={() => handleDelete(record.id)}
                      okText="Sì"
                      cancelText="No"
                    >
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )
                ] : []),
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={6}>
                    <CarOutlined />
                    <strong>{record.targa}</strong>
                    {record.courtesy_car && <Tag color="green" style={{ fontSize: '10px' }}>Cortesia</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    <span>{record.marca} {record.modello}{record.anno ? ` (${record.anno})` : ''}</span>
                    <span style={{ color: '#888' }}>{getCustomerName(record.current_customer_id)}</span>
                  </Space>
                }
              />
            </List.Item>
          )}
          pagination={{
            pageSize: 20,
            showTotal: (total) => `Totale ${total} veicoli`,
            size: 'small',
          }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={sortedItems.slice((page - 1) * pageSize, page * pageSize)}
          rowKey="id"
          loading={isLoading}
          onChange={(_pag, _filters, sorter: any, extra: any) => {
            if (extra?.action === 'sort') {
              const newField = sorter?.order ? (sorter.field || sorter.column?.dataIndex || 'targa') : undefined
              const newOrder = sorter?.order || undefined
              setSortField(newField)
              setSortOrder(newOrder)
              setPage(1)
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total: sortedItems.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Totale ${total} veicoli`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage)
              setPageSize(newPageSize)
            },
          }}
        />
      )}

      <VehicleFormModal
        open={isModalOpen}
        editingVehicle={editingVehicle}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingVehicle(null)
        }}
        onSuccess={() => {
          setIsModalOpen(false)
          setEditingVehicle(null)
        }}
      />

      <VehicleMaintenanceDrawer
        vehicle={maintenanceVehicle}
        onClose={() => setMaintenanceVehicle(null)}
      />
    </div>
  )
}

export default VehiclesPage
